import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MessageSquare, 
  Send, 
  Loader2,
  ArrowLeft,
  FileText,
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Mic,
  MicOff,
  Search,
  Brain
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Loading messages that cycle through
const loadingMessages = [
  { icon: Search, text: "Søger i produktdokumenter..." },
  { icon: FileText, text: "Finder relevante informationer..." },
  { icon: Brain, text: "Analyserer og formulerer svar..." }
];

const ProductKnowledgePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [indexStatus, setIndexStatus] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const recognitionRef = useRef(null);
  const loadingIntervalRef = useRef(null);

  useEffect(() => {
    fetchIndexStatus();
    fetchHistory();
    
    // Check for speech recognition support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'da-DK'; // Danish
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setQuestion(transcript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [token]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchIndexStatus = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/product-knowledge/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndexStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch index status:', err);
    }
  };

  const fetchHistory = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/product-knowledge/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setQuestion(''); // Clear previous text
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading) return;
    
    const userQuestion = question.trim();
    setQuestion('');
    
    // Add user message to conversation
    setConversation(prev => [...prev, { type: 'user', text: userQuestion }]);
    setLoading(true);
    setLoadingStep(0);
    
    // Start cycling through loading messages
    loadingIntervalRef.current = setInterval(() => {
      setLoadingStep(prev => (prev + 1) % loadingMessages.length);
    }, 1500);
    
    try {
      const response = await axios.post(
        `${API}/product-knowledge/ask`,
        { question: userQuestion },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add AI response to conversation
      setConversation(prev => [...prev, { 
        type: 'assistant', 
        text: response.data.answer,
        sources: response.data.sources || [],
        success: response.data.success
      }]);
      
      // Refresh history
      fetchHistory();
      
    } catch (err) {
      console.error('Failed to get answer:', err);
      setConversation(prev => [...prev, { 
        type: 'assistant', 
        text: err.response?.data?.detail || 'Der opstod en fejl. Prøv igen.',
        success: false
      }]);
    } finally {
      setLoading(false);
      setLoadingStep(0);
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
      inputRef.current?.focus();
    }
  };

  const handleHistoryClick = (historyItem) => {
    setQuestion(historyItem.question);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  const exampleQuestions = [
    "Hvad er min og max mål på rullegardin 3000?",
    "Hvor stor kan en Novus plissédør laves?",
    "Hvor stor kan en BB24 laves?"
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-slate-900 text-white px-4 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white hover:bg-slate-800 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold font-outfit flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-teal-400" />
              Spørg Gardinbussen
            </h1>
            <p className="text-slate-400 text-sm">AI-assistent til alle spørgsmål</p>
          </div>
          {indexStatus && (
            <div className="text-xs text-slate-400">
              {indexStatus.document_count || 0} dokumenter
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-2xl mx-auto w-full">
        {/* Welcome message if no conversation */}
        {conversation.length === 0 && (
          <div className="space-y-4">
            <Card className="border-slate-200 bg-gradient-to-br from-teal-50 to-slate-50">
              <CardContent className="py-6 text-center">
                <Sparkles className="h-12 w-12 text-teal-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-slate-800 mb-2">
                  Velkommen til Spørg Gardinbussen
                </h2>
                <p className="text-slate-600 text-sm mb-4">
                  Stil spørgsmål om produkter, manualer, telefonlister og mere.
                  AI'en søger i alle dokumenter og giver præcise svar.
                </p>
              </CardContent>
            </Card>

            {/* Example questions */}
            <div className="space-y-2">
              <p className="text-sm text-slate-500 font-medium">Prøv f.eks.:</p>
              <div className="grid gap-2">
                {exampleQuestions.map((q, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="justify-start text-left h-auto py-2 px-3 text-sm"
                    onClick={() => {
                      setQuestion(q);
                      inputRef.current?.focus();
                    }}
                    data-testid={`example-q-${i}`}
                  >
                    <MessageSquare className="h-4 w-4 mr-2 flex-shrink-0 text-slate-400" />
                    {q}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Conversation */}
        {conversation.map((msg, i) => (
          <div 
            key={i}
            className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                msg.type === 'user' 
                  ? 'bg-teal-500 text-white rounded-br-md' 
                  : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
              }`}
            >
              <p className="whitespace-pre-wrap text-sm">{msg.text}</p>
              
              {/* Sources */}
              {msg.sources && msg.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200/50">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    Kilder: {msg.sources.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator - Enhanced typing animation with progressive messages */}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%] shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  {(() => {
                    const CurrentIcon = loadingMessages[loadingStep].icon;
                    return <CurrentIcon className="h-4 w-4 text-teal-500" />;
                  })()}
                  <span className="text-sm font-medium">{loadingMessages[loadingStep].text}</span>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-teal-500 rounded-full transition-all duration-500"
                    style={{ width: `${((loadingStep + 1) / loadingMessages.length) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-slate-400">{loadingStep + 1}/{loadingMessages.length}</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* History toggle */}
      {history.length > 0 && (
        <div className="px-4 max-w-2xl mx-auto w-full">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="w-full text-slate-500 hover:text-slate-700"
          >
            {showHistory ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronUp className="h-4 w-4 mr-1" />}
            Tidligere spørgsmål
          </Button>
          
          {showHistory && (
            <div className="bg-white border border-slate-200 rounded-lg p-2 mt-1 mb-2 max-h-40 overflow-y-auto">
              {history.map((item, i) => (
                <button
                  key={i}
                  onClick={() => handleHistoryClick(item)}
                  className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded truncate"
                >
                  {item.question}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-slate-200 bg-white flex-shrink-0">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div className="flex gap-2">
            {/* Voice Input Button */}
            {speechSupported && (
              <Button
                type="button"
                onClick={toggleVoice}
                disabled={loading}
                variant={isListening ? "default" : "outline"}
                className={`${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}`}
                data-testid="voice-btn"
              >
                {isListening ? (
                  <MicOff className="h-5 w-5" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </Button>
            )}
            
            <Input
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={isListening ? "Lytter..." : "Stil et spørgsmål om produkter..."}
              className={`flex-1 ${isListening ? 'border-red-300 bg-red-50' : ''}`}
              disabled={loading}
              data-testid="question-input"
            />
            <Button 
              type="submit" 
              disabled={!question.trim() || loading}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="send-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          
          {/* Voice feedback */}
          {isListening && (
            <p className="text-center text-sm text-red-500 mt-2 animate-pulse">
              🎤 Taler... Tryk igen for at stoppe
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProductKnowledgePage;
